import { Image } from 'expo-image';
import Head from 'expo-router/head';
import { Link } from 'expo-router';
import { useCallback, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { listSeedScript } from '@/components/detail/list-seed';
import { BoatCard } from '@/components/riviera/boat-card';
import { HubFilterStatus, HubFilters, boatFilterGroups } from '@/components/riviera/hub-filters';
import {
  Faq,
  MoreLink,
  SectionHeading,
  landingStyles,
} from '@/components/riviera/landing-parts';
import { LoadMoreButton } from '@/components/riviera/load-more-button';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Fonts, MaxContentWidth, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { useBoats } from '@todo/core/hooks/use-boats';
import { useFilteredHub } from '@todo/core/hooks/use-filtered-hub';
import { cities } from '@/lib/events-data';
import { DEFAULT_BOAT_FILTERS, isDefaultFilters, type BoatFilters } from '@todo/core/hub-filters';
import { fetchBoatsPage, fetchFilteredBoats } from '@todo/core/hub-pages';
import { boatFaq, localizedBoatTitle } from '@/lib/i18n/boat-content';
import { boatRentalCityCopy } from '@/lib/i18n/boat-rental-content';
import { boatsHubText } from '@/lib/i18n/hub-content';
import type { Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import {
  BOATS_INDEX_QUERY,
  BOATS_INDEX_SEED_KEY,
  BOAT_RENTAL_CITIES_SEED_KEY,
  LIST_BUCKET,
} from '@todo/core/list-queries';
import {
  absoluteUrl,
  boatsCrumb,
  breadcrumbJsonLd,
  faqPageJsonLd,
  hreflangLinks,
  itemListJsonLd,
  richResultImage,
} from '@todo/core/seo';
import { getPrefetched } from '@todo/core/static-prefetch';

// City chips — real links to each city's /boat-rental/[city] landing page (not
// an in-page filter), so the hub is the crawl entry point to every city's
// full boat list. Only cities that page was exported for are listed.
function CityChips({ cityIds, locale }: { cityIds: string[]; locale: Locale }) {
  const theme = useTheme();
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.chipsRow}>
      {cityIds.map((id) => {
        const city = cities.find((c) => c.id === id);
        if (!city) return null;
        return (
          <Link
            key={city.id}
            href={{ pathname: '/[lang]/boat-rental/[city]', params: { lang: locale, city: city.id } }}
            asChild>
            <Pressable accessibilityRole="link">
              {({ pressed }) => (
                // Pill styling on an inner View: <Link asChild> drops a
                // Pressable's function-style `style` on web.
                <View
                  style={[
                    styles.chip,
                    { backgroundColor: theme.card, borderColor: theme.border },
                    pressed && { opacity: 0.85 },
                  ]}>
                  <Image source={city.image} style={styles.cityImg} contentFit="cover" />
                  <Text style={[styles.chipText, { color: theme.text }]}>{city.name}</Text>
                </View>
              )}
            </Pressable>
          </Link>
        );
      })}
    </ScrollView>
  );
}

export default function BoatsScreen() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { boats, loading, error } = useBoats(BOATS_INDEX_QUERY, BOATS_INDEX_SEED_KEY);
  const [filters, setFilters] = useState<BoatFilters>(DEFAULT_BOAT_FILTERS);
  const hub = useFilteredHub(
    boats,
    fetchBoatsPage,
    filters,
    isDefaultFilters(filters, DEFAULT_BOAT_FILTERS),
    fetchFilteredBoats,
  );
  const more = hub.more;
  const setFilter = useCallback(
    (id: string, value: string) => setFilters((prev) => ({ ...prev, [id]: value })),
    [],
  );
  const resetFilters = useCallback(() => setFilters(DEFAULT_BOAT_FILTERS), []);
  // The exact city pages the export generated (baked below); on native, where
  // every route resolves, all cities.
  const [bakedCityIds] = useState(() =>
    getPrefetched<string[]>(LIST_BUCKET, BOAT_RENTAL_CITIES_SEED_KEY),
  );
  const cityIds = bakedCityIds ?? cities.map((c) => c.id);

  const canonical = absoluteUrl(locale, 'boats');
  const ogImage = richResultImage(boats[0]?.image_url);
  const faq = boatFaq(locale);
  // Hand-written, evergreen SEO copy — see hub-content.ts.
  const copy = boatsHubText(locale);
  // "Boat Rental in Nice"-style anchors to every exported city page — stronger
  // anchor text than the chips' bare city names.
  const cityLinks = cityIds.flatMap((id) => {
    const cityCopy = boatRentalCityCopy(locale, id);
    return cityCopy ? [{ id, label: cityCopy.title }] : [];
  });

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      <Head key={locale}>
        <title>{t('seo.boatsTitle')}</title>
        <meta name="description" content={t('seo.boatsDescription')} />
        <link rel="canonical" href={canonical} />
        {hreflangLinks('boats').map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t('seo.boatsTitle')} />
        <meta property="og:description" content={t('seo.boatsDescription')} />
        <meta property="og:url" content={canonical} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta name="twitter:title" content={t('seo.boatsTitle')} />
        <meta name="twitter:description" content={t('seo.boatsDescription')} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [{ name: boatsCrumb(locale).name }]))}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(faq))}</script>
        {boats.length > 0 ? (
          <script type="application/ld+json">
            {JSON.stringify(
              itemListJsonLd(
                boats.map((boat) => ({
                  name: localizedBoatTitle(boat, locale),
                  url: absoluteUrl(locale, `boats/${boat.id}`),
                })),
                canonical,
              ),
            )}
          </script>
        ) : null}
        {listSeedScript(BOATS_INDEX_SEED_KEY)}
        {listSeedScript(BOAT_RENTAL_CITIES_SEED_KEY)}
      </Head>
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView
        style={{ flex: 1 }}>
        <View style={styles.centered}>
          <View style={styles.page}>
            <Text
              role="heading"
              aria-level={1}
              style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {t('boats.headingRegion')}
            </Text>
            {copy.intro.map((paragraph, index) => (
              <Text key={index} style={[landingStyles.intro, { color: theme.text }]}>
                {paragraph}
              </Text>
            ))}

            <CityChips cityIds={cityIds} locale={locale} />

            {loading ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('boats.loading')}
              </Text>
            ) : error ? (
              <Text style={[styles.status, { color: theme.destructive }]}>{error}</Text>
            ) : boats.length === 0 ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('boats.empty')}
              </Text>
            ) : (
              <>
                {/* Filters work after hydration only: the static HTML is always
                    the unfiltered seed (see useFilteredHub). */}
                <HubFilters
                  groups={boatFilterGroups(filters, t)}
                  onChange={setFilter}
                  onReset={resetFilters}
                />
                <HubFilterStatus
                  filtered={hub.filtered}
                  total={hub.total}
                  loading={hub.loading}
                  error={hub.error}
                  countLabel={(count) => t('hubFilters.boatsCount', { count })}
                  onRetry={hub.retry}
                  onReset={resetFilters}
                />
                <View
                  aria-busy={hub.loading}
                  style={[styles.grid, hub.loading && styles.stale]}>
                  {hub.rows.map((boat, index) => (
                    <BoatCard key={boat.id} boat={boat} eager={index < 4} />
                  ))}
                  {more.extra.map((boat) => (
                    <BoatCard key={boat.id} boat={boat} eager={false} />
                  ))}
                </View>
                {more.canLoadMore && !hub.loading ? (
                  <LoadMoreButton onPress={more.loadMore} loading={more.loading} error={more.error} />
                ) : null}
              </>
            )}

            {/* Below the cards so the LCP image stays near the top. */}
            {copy.sections.map((section) => (
              <View key={section.heading} style={landingStyles.section}>
                <SectionHeading>{section.heading}</SectionHeading>
                {section.paragraphs.map((paragraph, index) => (
                  <Text key={index} style={[landingStyles.intro, { color: theme.text }]}>
                    {paragraph}
                  </Text>
                ))}
              </View>
            ))}

            {cityLinks.length > 0 ? (
              <View style={landingStyles.section}>
                <SectionHeading>{copy.citiesHeading}</SectionHeading>
                {cityLinks.map((link) => (
                  <MoreLink
                    key={link.id}
                    label={link.label}
                    href={{ pathname: '/[lang]/boat-rental/[city]', params: { lang: locale, city: link.id } }}
                  />
                ))}
              </View>
            ) : null}

            {/* The visible counterpart of the FAQPage JSON-LD above — Google
                requires the two to match. */}
            <Faq heading={t('boats.faqHeading')} entries={faq} />
          </View>
        </View>
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.four,
    gap: Spacing.three,
  },
  heading: {
    fontSize: 26,
    fontWeight: '700',
  },
  chipsRow: {
    gap: Spacing.one + 2,
    paddingRight: Spacing.three,
    paddingVertical: 2,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingLeft: 6,
    paddingRight: Spacing.two,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: 999,
  },
  cityImg: {
    width: 22,
    height: 22,
    borderRadius: 999,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '500',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.three,
    justifyContent: 'center',
  },
  status: {
    fontSize: 14,
    paddingVertical: Spacing.four,
  },
  stale: {
    opacity: 0.5,
  },
});
